import Player from '../Player';
import { Weapon } from '../Weapon';
import { InputHost } from './FirstPersonInput';
import { aimPoint, movementAngle, normalizeAngle } from './World3D';

type Stick = { id: number | null; startX: number; startY: number; x: number; y: number };
const stick = (): Stick => ({ id: null, startX: 0, startY: 0, x: 0, y: 0 });
const DEAD_ZONE = 8;
const FIRE_DISTANCE = 24;

/** FPS touch controls using the existing circles; Controller retains the original 2D listeners. */
export default class TouchInput3D {
	private move = stick();
	private aim = stick();
	private hitId: number | null = null;
	private yaw = 0;
	private playerId: number | null = null;
	private gameId = -1;
	private lastTick: number | null = null;
	private lastAim = -1;
	private lastAimAt = 0;
	private lastMove: number | null = null;
	private actionWeapon: Weapon | null = null;
	private actionGameId = -1;
	private pressedAt = 0;
	private lastFireAt = 0;
	private releaseTimer: number | null = null;
	private releaseGameId = -1;
	private cleanups: (() => void)[] = [];
	private labels: HTMLElement[];
	private originalStyles: { left: string; top: string; opacity: string }[];

	constructor(private moveCircle: HTMLElement, private aimCircle: HTMLElement,
		private hitButton: HTMLElement, private host: InputHost) {
		this.originalStyles = [moveCircle, aimCircle].map(circle => ({
			left: circle.style.left, top: circle.style.top, opacity: circle.style.opacity,
		}));
		this.labels = [moveCircle, aimCircle].map((circle, index) => {
			const label = document.createElement('div');
			label.textContent = index === 0 ? 'Pohyb' : '← otáčení → · ↑ útok';
			label.style.cssText = 'position:absolute;bottom:calc(100% + 6px);left:50%;transform:translateX(-50%);white-space:nowrap;color:white;text-shadow:0 1px 3px black;font:12px sans-serif;pointer-events:none;display:none';
			circle.insertAdjacentElement('afterend', label);
			return label;
		});
		this.listen(moveCircle, 'touchstart', event => this.start(this.move, event));
		this.listen(aimCircle, 'touchstart', event => this.start(this.aim, event));
		this.listen(moveCircle, 'touchmove', event => this.drag(this.move, event));
		this.listen(aimCircle, 'touchmove', event => this.drag(this.aim, event));
		this.listen(hitButton, 'touchstart', event => {
			if (!this.canAct() || this.hitId !== null) return;
			const touch = event.changedTouches[0];
			if (!touch) return;
			event.preventDefault();
			this.initialize(this.host.player());
			this.hitId = touch.identifier;
			this.syncAction(Date.now());
		});
		this.listen(document, 'touchend', event => this.end(event, true));
		this.listen(document, 'touchcancel', event => this.end(event, false));
		window.addEventListener('blur', this.reset);
		document.addEventListener('visibilitychange', this.visibilityChange);
	}

	private listen(target: EventTarget, name: string, listener: (event: TouchEvent) => void): void {
		target.addEventListener(name, listener, { passive: false });
		this.cleanups.push(() => target.removeEventListener(name, listener));
	}

	canAct(): boolean {
		return this.host.enabled() && this.host.controllable() && !!this.host.player()?.alive();
	}

	private initialize(player: Player): void {
		if (this.playerId === player.id && this.gameId === this.host.gameId()) return;
		this.reset();
		this.playerId = player.id;
		this.gameId = this.host.gameId();
		this.yaw = player.getAngle();
		this.lastAim = Math.round(normalizeAngle(this.yaw)) % 360;
		this.host.setYaw(this.yaw);
	}

	update(player: Player, spectating: boolean, now = Date.now()): void {
		if (!this.canAct() || spectating || !player.alive()) { this.reset(); return; }
		this.initialize(player);
		const seconds = this.lastTick === null ? 0 : Math.max(0, Math.min(100, now - this.lastTick)) / 1000;
		this.lastTick = now;
		const turn = Math.abs(this.aim.x) <= DEAD_ZONE ? 0
			: Math.sign(this.aim.x) * Math.min(1, (Math.abs(this.aim.x) - DEAD_ZONE) / 52);
		this.yaw = normalizeAngle(this.yaw + turn * 180 * seconds);
		this.host.setYaw(this.yaw);
		this.sendAim(now);
		this.sendMove();
		this.syncAction(now);
		for (const label of this.labels) label.style.display = 'block';
	}

	private start(control: Stick, event: TouchEvent): void {
		if (!this.canAct() || control.id !== null) return;
		const touch = event.changedTouches[0];
		if (!touch) return;
		event.preventDefault();
		this.initialize(this.host.player());
		control.id = touch.identifier;
		control.startX = touch.clientX;
		control.startY = touch.clientY;
		control.x = control.y = 0;
		this.visuals();
	}

	private drag(control: Stick, event: TouchEvent): void {
		if (control.id === null) return;
		if (!this.canAct()) { this.reset(); return; }
		const touch = Array.from(event.touches).find(touch => touch.identifier === control.id);
		if (!touch) return;
		event.preventDefault();
		control.x = touch.clientX - control.startX;
		control.y = touch.clientY - control.startY;
		this.sendMove();
		this.syncAction(Date.now());
		this.visuals();
	}

	private end(event: TouchEvent, allowThrow: boolean): void {
		const ended = Array.from(event.changedTouches).map(touch => touch.identifier);
		if (![this.move.id, this.aim.id, this.hitId].some(id => id !== null && ended.includes(id))) return;
		event.preventDefault();
		if (!this.canAct()) { this.reset(); return; }
		for (const control of [this.move, this.aim]) if (ended.includes(control.id)) {
			control.id = null;
			control.x = control.y = 0;
		}
		if (ended.includes(this.hitId)) this.hitId = null;
		this.sendMove();
		this.syncAction(Date.now(), allowThrow);
		this.visuals();
	}

	private sendAim(now: number, force = false): void {
		const angle = Math.round(this.yaw) % 360;
		if (angle === this.lastAim || (!force && now - this.lastAimAt < 25)) return;
		this.host.emit('a', this.gameId, angle);
		this.lastAim = angle;
		this.lastAimAt = now;
	}

	private sendMove(): void {
		const angle = Math.hypot(this.move.x, this.move.y) > DEAD_ZONE
			? Math.round(movementAngle(this.yaw, -this.move.y, this.move.x)) % 360 : null;
		if (angle === this.lastMove) return;
		this.host.emit('moveAngle', this.gameId, angle !== null, angle === null ? undefined : angle);
		this.lastMove = angle;
	}

	private isThrow(weapon: Weapon): boolean { return weapon === Weapon.Grenade || weapon === Weapon.Smoke; }

	private syncAction(now: number, allowThrow = true): void {
		const player = this.host.player();
		const firing = this.hitId !== null || (this.aim.id !== null && this.aim.y < -FIRE_DISTANCE);
		if (this.actionWeapon !== null && player.getWeapon() !== this.actionWeapon) this.endAction(false, now);
		if (!firing || player.getWeapon() === Weapon.Empty) { this.endAction(allowThrow, now); return; }
		if (this.actionWeapon === null) {
			this.clearRelease();
			this.actionWeapon = player.getWeapon();
			this.actionGameId = this.gameId;
			this.pressedAt = this.lastFireAt = now;
			if (!this.isThrow(this.actionWeapon)) this.fire(now);
		} else if (!this.isThrow(this.actionWeapon) && now - this.lastFireAt >= 200) this.fire(now);
	}

	private fire(now: number): void {
		const player = this.host.player();
		this.sendAim(now, true);
		this.host.emit('m', this.actionGameId, 'l', aimPoint(player.getCenterX(), player.getCenterY(), this.yaw));
		this.lastFireAt = now;
	}

	private endAction(allowThrow: boolean, now: number): void {
		if (this.actionWeapon === null) return;
		const player = this.host.player();
		if (allowThrow && this.isThrow(this.actionWeapon) && this.canAct() && player.getWeapon() === this.actionWeapon) {
			this.sendAim(now, true);
			this.host.emit('m', this.actionGameId, 'l', aimPoint(player.getCenterX(), player.getCenterY(), this.yaw), Math.max(9, now - this.pressedAt));
			this.releaseGameId = this.actionGameId;
			this.releaseTimer = window.setTimeout(() => {
				this.host.emit('m', this.releaseGameId, '-l');
				this.releaseTimer = null;
			}, 16);
		} else this.host.emit('m', this.actionGameId, '-l');
		this.actionWeapon = null;
	}

	private clearRelease(): void {
		if (this.releaseTimer === null) return;
		window.clearTimeout(this.releaseTimer);
		this.releaseTimer = null;
		this.host.emit('m', this.releaseGameId, '-l');
	}

	private visuals(): void {
		[this.move, this.aim].forEach((control, index) => {
			const circle = index === 0 ? this.moveCircle : this.aimCircle;
			const length = Math.hypot(control.x, control.y);
			const scale = length > 20 ? 20 / length : 1;
			circle.style.left = `${control.x * scale}px`;
			circle.style.top = `${control.y * scale}px`;
			circle.style.opacity = (index === 0 ? length > DEAD_ZONE : control.y < -FIRE_DISTANCE) ? '0.5' : '0.15';
		});
	}

	private visibilityChange = (): void => { if (document.hidden) this.reset(); };

	reset = (): void => {
		if (this.playerId === null) return; // An unused 3D controller must not alter the 2D circles or camera.
		if (this.lastMove !== null) this.host.emit('moveAngle', this.gameId, false);
		this.endAction(false, Date.now());
		this.clearRelease();
		for (const control of [this.move, this.aim]) { control.id = null; control.x = control.y = 0; }
		this.hitId = null;
		this.lastMove = this.lastTick = this.playerId = null;
		this.lastAim = -1;
		this.lastAimAt = 0;
		this.host.setYaw(null);
		for (const label of this.labels) label.style.display = 'none';
		[this.moveCircle, this.aimCircle].forEach((circle, index) => Object.assign(circle.style, this.originalStyles[index]));
	};

	dispose(): void {
		this.reset();
		for (const cleanup of this.cleanups) cleanup();
		window.removeEventListener('blur', this.reset);
		document.removeEventListener('visibilitychange', this.visibilityChange);
		for (const label of this.labels) label.remove();
	}
}
